import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowDownLeft, ArrowUpRight, GalleryHorizontalEnd } from "lucide-react";
import { packEnclose, packSiblings } from "d3";
import { marketTotalColor } from "../../services/mindshareMarket";
import "./tiktokViewMode.css";

const previewNumber = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const total = (game) => Number(game.viewsTotal ?? game.views ?? 0);
const previewName = name => name.replace(/^Garena Free Fire$/, "Free Fire").replace(/\s*\(PUBG\)/g, "");
const truncatedTotal = game => `${previewNumber.format(total(game)).slice(0, 6).replace(/,$/, "")}...`;
const variation = index => {
  const value = Math.sin((index + 1) * 127.1) * 43758.5453;
  return value - Math.floor(value);
};
const previewCovers = [
  "23020_IGDB", "502732_IGDB", "27471_IGDB", "494184_IGDB",
  "33214", "493057", "513181_IGDB",
].map(id => ({ imageUrl: `https://static-cdn.jtvnw.net/ttv-boxart/${id}-285x380.jpg` }));
const previewBubbleWeights = Array.from({ length: 1007 }, (_, index) => ({ views: 1000000 / (index + 1) }));

function Drafting({ variant = "full" }) {
  return <svg className={`ttx-drafting ttx-drafting-${variant}`} viewBox="0 0 480 500" fill="none" aria-hidden="true">
    <path d="M22 0v478h458M0 24h456v476M48 62h379v372H48zM76 90h323v316H76z" />
    <path d="M0 250h480M240 0v500M22 478 456 24M399 90l28-28M48 434l28-28" strokeDasharray="2 7" />
    <path className="ttx-signal" d="M22 340V24h164m180 0h90v160" />
    <path d="M10 24h24M22 12v24M444 478h24m-12-12v24M228 250h24m-12-12v24" />
    <circle cx="240" cy="250" r="152" strokeDasharray="1 9" />
  </svg>;
}

function CardStudy({ games, loading }) {
  const covers = loading && !games.length ? previewCovers :
    games.filter((game) => game.hasMetadata && game.imageUrl).slice(0, 7);
  return <div className="ttx-study ttx-card-study" aria-hidden="true">
    <Drafting />
    <span className="ttx-art-note">CATALOG / CULTURAL SIGNALS</span>
    <div className="ttx-cover-stack">
      {covers.map((game, index) => (
        <div className={`ttx-cover ttx-cover-${index}`} key={game.selectionKey || game.imageUrl}>
          <img src={game.imageUrl} alt="" draggable="false" loading="eager" decoding="async" />
        </div>
      ))}
    </div>
    <div className="ttx-coordinate">↗ ARTWORK / LIVE CATALOG</div>
  </div>;
}

function TableStudy({ games, loading }) {
  return <div className="ttx-study ttx-table-study" aria-hidden="true">
    <Drafting />
    <span className="ttx-art-note">SIGNAL / STRUCTURE / CONTEXT</span>
    <div className="ttx-data-sheet">
      <div className="ttx-sheet-title"><span>THE SIGNAL INDEX</span><span>↓ / TOTAL VIEWS</span></div>
      <div className="ttx-sheet-columns"><span>GAME / TITLE</span><span>TOTAL ↓</span></div>
      {loading && !games.length && Array.from({ length: 6 }, (_, index) => (
        <div className="ttx-data-row ttx-abstract-row" key={index}>
          <i style={{ width: `${65 - index * 6}%` }} /><i />
        </div>
      ))}
      {games.slice(0, 6).map((game, index) => <div className={`ttx-data-row ${index === 2 ? "is-marked" : ""}`} key={game.selectionKey || game.name}>
        <span className="ttx-row-index">{String(index + 1).padStart(2, "0")}</span>
        <span className="ttx-row-name">{previewName(game.name)}</span>
        <span className="ttx-row-distribution"><i style={{ width: `${100 * Math.sqrt(total(game) / Math.max(1, total(games[0])))}%` }} /></span>
        <span className="ttx-row-value">{truncatedTotal(game)}</span>
        <span className="ttx-row-bar" style={{ width: `${Math.max(3, total(game) / Math.max(1, total(games[0])) * 100)}%` }} />
      </div>)}
      <div className="ttx-sheet-foot">REAL TITLES / ALL-TIME VIEWS</div>
    </div>
    <div className="ttx-coordinate">↗ PRECISION / WITHOUT THE NOISE</div>
  </div>;
}

// A bounded, non-interactive sample, not the full Market Mind layout.
function bubbleStudy(games) {
  if (!games.length) return [];
  const max = Math.max(1, ...games.map(total));
  const rest = games.slice(7);
  const count = Math.min(1000, rest.length);
  const sample = [...games.slice(0, 7), ...Array.from({ length: count }, (_, index) =>
    rest[Math.floor((count - 1 - index) * rest.length / count)])];
  // Stable variation keeps the illustration mixed without changing on rerenders.
  const circles = sample.map((game, index) => ({
    r: 0.6 + 10 * Math.pow(total(game) / max, 0.24),
    variation: variation(index),
  })).sort((a, b) => a.r * (0.55 + a.variation) - b.r * (0.55 + b.variation));
  const largestRadius = Math.max(...circles.map(circle => circle.r));
  packSiblings(circles);
  const enclosure = packEnclose(circles);
  const scale = 124 / enclosure.r;
  return circles.map(({ x, y, r, variation: noise }, index) => {
    const distance = Math.min(1, (Math.hypot(x - enclosure.x, y - enclosure.y) + r * 0.4) / enclosure.r);
    const level = Math.max(r > largestRadius * 0.55 ? (noise > 0.8 ? 0.95 : noise > 0.6 ? 0.8 : 0.4) : 0,
      Math.min(1, 1 - distance + (noise - 0.5) * 0.8));
    return {
      x: 128 + (x - enclosure.x) * scale,
      y: 128 + (y - enclosure.y) * scale,
      r: Math.max(0.1, r * scale - 0.25), index,
      color: marketTotalColor(Math.expm1(8 * level), Math.expm1(8)),
    };
  });
}

function MarketStudy({ games, loading }) {
  const bubbles = useMemo(() => bubbleStudy(loading && !games.length ? previewBubbleWeights : games), [games, loading]);
  return <div className="ttx-study ttx-market-study" aria-hidden="true">
    <Drafting />
    <span className="ttx-art-note">THE MARKET / IN A DIFFERENT LIGHT</span>
    <svg className="ttx-bubble-field" viewBox="-16 -16 288 288" aria-hidden="true">
      <path d="M-12 40v-52h52m176 0h52v52M-12 216v52h52m176 0h52v-52" stroke="currentColor" fill="none" />
      {bubbles.map(({ x, y, r, color, index }) => <circle key={index} cx={x} cy={y} r={r} fill={color} />)}
      <path d="M-16 128h288M128-16v288" stroke="#d7f0e2" strokeOpacity=".22" strokeDasharray="1 5" />
    </svg>
    <span className="ttx-market-axis">TOTAL VIEWS →</span>
    <div className="ttx-market-key"><i /><span>LOW</span><span>TOTAL SIGNAL</span><span>HIGH</span><i /></div>
    <div className="ttx-coordinate">↗ BUBBLE STUDY / {loading && !games.length ? "ILLUSTRATIVE PREVIEW" : "LIVE TOTALS"}</div>
  </div>;
}

const exhibits = [
  { view: "cards", title: "Cards", subtitle: "Discover Games", caption: "The artwork. The rankings. The next game worth watching.", Study: CardStudy },
  { view: "table", title: "Table", subtitle: "Analyze the Data", caption: "Go row by row. Compare the signals behind the stories.", Study: TableStudy },
  { view: "market", title: "Market Mind", subtitle: "Explore the Market", caption: "Step back. See how the entire gaming conversation fits together.", Study: MarketStudy },
];

export default function TikTokViewExhibition({ games, loading, error, onRetry, onChoose }) {
  const heading = useRef(null);
  useEffect(() => { heading.current?.focus({ preventScroll: true }); }, []);
  const sample = useMemo(() => [...games].filter((game) => total(game) > 0).sort((a, b) => total(b) - total(a)), [games]);
  return <section className="ttx-exhibition" aria-labelledby="ttx-title" style={{ "--ttx-grain": `url("${process.env.PUBLIC_URL}/redesign/images/redesign/blade_v1/art/05_global_print_grain_overlay.png")` }}>
    <div className="ttx-exhibition-top">
      <span className="ttx-eyebrow"><span className="ttx-section-index">01</span><span>/</span> VIEWFINDER</span>
    </div>
    <div className="ttx-introduction">
      <h1 id="ttx-title" tabIndex={-1} ref={heading}>A different way<br className="ttx-mobile-break" /> to <em>see the signal.</em></h1>
    </div>
    {error && <div className="ttx-data-alert" role="alert">TikTok data could not refresh: {error}. <button type="button" onClick={onRetry}>Retry TikTok data</button></div>}
    {!loading && !error && !sample.length && <p role="status">No TikTok titles are available in the current publication. You can still open any view.</p>}
    <div className="ttx-exhibits">
      {exhibits.map(({ view, title, subtitle, caption, Study }, index) => (
        <button className={`ttx-exhibit ttx-exhibit-${view}`} type="button" key={view} onClick={() => onChoose(view)} aria-label={`Open ${title}: ${subtitle}`}>
          <div className="ttx-panel-meta"><span>0{index + 1} / {subtitle.toUpperCase()}</span><ArrowUpRight size={20} /></div>
          <Study games={sample} loading={loading} />
          <div className="ttx-panel-copy"><h2>{title}</h2><p>{caption}</p><span className="ttx-open">EXPLORE {title.toUpperCase()} <ArrowUpRight size={17} /></span></div>
        </button>
      ))}
    </div>
    <footer className="ttx-exhibition-footer"><span><ArrowDownLeft size={14} /> THREE LENSES. SAME LIVE DATA.</span><span>CHANGE YOUR VIEW. KEEP YOUR CONTEXT.</span></footer>
  </section>;
}

export function TikTokViewfinderButton({ open, onClick }) {
  const [target, setTarget] = useState(null);
  const button = useRef(null);
  const previouslyOpen = useRef(open);
  useEffect(() => {
    if (previouslyOpen.current && !open) button.current?.focus({ preventScroll: true });
    previouslyOpen.current = open;
  }, [open]);
  useEffect(() => {
    const findTarget = () => setTarget(document.getElementById("tiktok-header-viewfinder"));
    findTarget();
    const observer = new MutationObserver(findTarget);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  if (!target) return null;
  return createPortal(<button ref={button} className="ttx-viewfinder-toggle" type="button" role="switch" aria-label="Viewfinder" aria-checked={open} onClick={onClick} title={open ? "Close Viewfinder" : "Open Viewfinder"}>
    <GalleryHorizontalEnd size={15} aria-hidden="true" />
    <span>VIEWFINDER</span><span className="ttx-toggle-track" aria-hidden="true"><span /></span>
  </button>, target);
}
